/**
 * SpPoolReadiness — "can a v2 (pooled-SP) run actually sign in?"
 *
 * v2 (`carrier_mortgagee_update_v2`) does NOT use the caller's per-user
 * dedicated-SP credentials. It leases a SLOT from the shared Epic SP pool and
 * loads that slot's `epic-sp-NN-credentials`. So the launch gate must ask about
 * the POOL, not about the operator — see `usesSpPool` in the shared helper.
 *
 * Why this gate exists: in sp_pool mode a slot with no saved credentials raises
 * `EpicSpCredentialsMissing`, which the classifier surfaces as
 * `abort_reason="sp_credentials_missing"`. There is NO fallback to the
 * operator's personal login — the run just dies. Blocking at launch turns a
 * dead run into a sentence the operator can act on.
 *
 * Add-in copy; ora mirrors this (ui/CLAUDE.md parity).
 */
import { orchestratorApi } from "../services/orchestratorApi";

/**
 * True when the pool holds at least one provisioned slot.
 *
 * Presence IS provisioning: the server counts slots with `HasCredentials`, so
 * every slot in the count is a usable one. (An earlier version of this function
 * read a `has_credentials` field off the listing wire shape, where no such field
 * exists, which made readiness permanently false.)
 *
 * Deliberately does NOT require an *available* slot — the count ignores lease
 * state. A fully-leased pool is a WAIT (the lease queues the run and grants when
 * a slot frees), not a dead end, and blocking on it would refuse launches the
 * backend handles correctly. Only "nothing provisioned at all" is unlaunchable.
 */
export function isSpPoolReady(provisioned: number | null | undefined): boolean {
  return typeof provisioned === "number" && provisioned > 0;
}

/**
 * Outcome of a pool-readiness probe.
 *
 * `"error"` is deliberately DISTINCT from `"empty"` even though both block the
 * launch. Collapsing them (the original `catch { return false }`) told an
 * operator with two provisioned slots "No service principals in pool", which is
 * simply untrue and sends them to the SP Pool Management page to fix a problem
 * that isn't there. Reported 2026-08-19 with a healthy two-slot pool.
 *
 * The gate still fails CLOSED on `"error"` — an unknown pool must not launch a
 * run that would dead-end on `sp_credentials_missing`. Only the wording and the
 * retry affordance differ.
 */
export type SpPoolReadiness = "ready" | "empty" | "error";

/** Does this outcome permit a launch? Only an explicitly READY pool does. */
export function spPoolPermitsLaunch(r: SpPoolReadiness | null): boolean {
  return r === "ready";
}

/**
 * Probe pool readiness. Never throws.
 *
 * Asks the AUTHENTICATED-ONLY readiness endpoint, not the admin listing. This
 * used to call `getSpPool()`, which is CentralTeamMember-gated: every branch AM
 * (BranchLeader / Producer / Servicer) got a 403, fell to `"error"`, and had
 * their pooled-SP launch blocked against a healthy pool. That was reasoned as
 * correct on the grounds that a non-admin cannot provision the pool either — but
 * *using* the pool is not *administering* it, and SP mode is the launch default,
 * so in practice v2 was admin-only. Readiness needs a count, which discloses
 * no SP identity, so it does not need an admin's authorization.
 *
 * Still fails CLOSED on `"error"`: an unknown pool must not launch a run that
 * would dead-end on `sp_credentials_missing`.
 */
export async function fetchSpPoolReadiness(): Promise<SpPoolReadiness> {
  try {
    return isSpPoolReady(await orchestratorApi.getSpPoolReadiness()) ? "ready" : "empty";
  } catch {
    return "error";
  }
}

/**
 * Boolean shim for callers that only need "can this launch?".
 *
 * @deprecated Prefer {@link fetchSpPoolReadiness} — a bare boolean cannot tell
 * an empty pool from a failed check, which is the bug this module now fixes.
 */
export async function fetchSpPoolReady(): Promise<boolean> {
  return spPoolPermitsLaunch(await fetchSpPoolReadiness());
}
